import { Store } from "@ngrx/store";
import { Component, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { toggleAnimation } from "src/app/shared/animations";
import { AuthService } from "src/app/auth/service/auth.service";
import { BillStatistics, ContractStatType } from "src/app/shared/types/statistics-types";
import { StatisticsService } from "src/app/shared/services/statistics.service";
import { forkJoin } from "rxjs";
import dayjs from "dayjs/esm";
import customParseFormat from "dayjs/esm/plugin/customParseFormat";

dayjs.extend(customParseFormat);

@Component({
  templateUrl: "./statistics.component.html",
  animations: [toggleAnimation],
  standalone: false,
})
export class StatisticsComponent implements OnInit {
  store: any;
  isLoading = false;
  columnChart: any;
  columnContractChart: any;

  filterOptions!: { label: string; value: string }[];
  selectedFilter = "None";
  yearOptions!: number[];
  selectedYear = new Date().getFullYear();

  constructor(
    public storeData: Store<any>,
    public authService: AuthService,
    public statsService: StatisticsService,
    public translate: TranslateService,
  ) {
  }

  ngOnInit() {
    const currentYear = new Date().getFullYear();
    this.yearOptions = Array.from(
      { length: currentYear - 2022 + 1 },
      (_, i) => 2022 + i,
    );

    forkJoin({
      none: this.translate.get("none"),
      running: this.translate.get("contract.filter_by_running"),
      stopped: this.translate.get("contract.filter_by_stopped"),
    }).subscribe((translations) => {
      this.filterOptions = [
        { label: translations.none, value: "None" },
        { label: translations.running, value: "Running" },
        { label: translations.stopped, value: "Stopped" },
      ];
    });
    this.updateCharts();

    this.translate.onLangChange.subscribe(() => {
      this.updateCharts();
    });
  }

  updateCharts() {
    this.isLoading = true;
    const subscription = this.statsService.getContractStatistics(this.selectedFilter).subscribe((data) => {
      subscription.unsubscribe();
      this.isLoading = false;
      this.columnContractChart = this.getContractStatisticsChartData(data);
    });
    this.loadBillStatistics();
  }

  onFilterChange() {
    this.updateCharts();
  }

  loadBillStatistics() {
    const subscription = this.statsService.getBillStatistics(this.selectedYear).subscribe((data) => {
      subscription.unsubscribe();
      this.columnChart = this.getSuperadminStatistics(data);
    });
  }

  getSuperadminStatistics(billStatistics: BillStatistics) {
    return {
      series: [
        {
          name: this.translate.instant("statistics.number_of_users"),
          type: "column",
          data: billStatistics.numberOfUsers,
        },
        {
          name: this.translate.instant("statistics.revenue"),
          type: "line",
          data: billStatistics.revenue,
        },
      ],
      chart: {
        height: 300,
        type: "line",
        stacked: false,
        zoom: {
          enabled: false,
        },
        toolbar: { show: false },
      },
      colors: ["#805dca", "#e7515a"],
      dataLabels: { enabled: false },
      stroke: { width: [0, 3] },
      xaxis: {
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      },
      yaxis: [
        {
          seriesName: this.translate.instant("statistics.number_of_users"),
          title: { text: this.translate.instant("statistics.number_of_users") },
        },
        {
          opposite: true,
          seriesName: this.translate.instant("statistics.revenue"),
          title: { text: this.translate.instant("statistics.revenue") },
        },
      ],
      tooltip: { shared: true, intersect: false },
    };
  }

  getContractStatisticsChartData(data: ContractStatType[]) {
    return {
      series: [
        {
          name: this.translate.instant("statistics.number_of_sales"),
          data: data.map(item => item.numberOfPlans),
        },
      ],
      chart: {
        height: 300,
        type: "bar",
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      colors: ["#e7515a"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          endingShape: "rounded",
        },
      },
      grid: {
        borderColor: "#e0e6ed",
      },
      xaxis: {
        categories: data.map(
          item =>
            `${item.companyName} - ${this.translate.instant(
              "statistics.from",
            )}: ${dayjs(item.instanceCreatedAt).format("DD.MM.YYYY")}`,
        ),
        axisBorder: {
          color: "#e0e6ed",
        },
      },
      yaxis: {
        opposite: false,
        labels: {
          offsetX: 0,
        },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter(val: any) {
            return val;
          },
        },
      },
    };
  }
}
